import { ImpactLensType } from '@upright/common/impact-lenses/types';
import { ImpactCategoryId } from '@upright/common/model/input/impacts/impact-categories';
import { pickScore } from '@upright/common/model/output/scores';
import { FinalImpactScoreType } from '@upright/common/model/output/scores/types';
import { orderedImpacts, Valence } from '@upright/common/model/types';
import { pickBy } from 'lodash';
import { parse, ParsedUrlQuery, stringify } from 'querystring';
import { ImpactLens } from '../../types';
import { NetImpactLensData, NetImpactLensOptions } from '../types';
import { useIsTeam } from '@upright/ui/common/generic/hooks';

export const useGetAvailableNumberRelatedViewOptionsKeys = () => {
  const isTeam = useIsTeam();
  if (isTeam) {
    return [...teamUserNumberRelatedViewOptionKeys, ...numberRelatedViewOptionKeys];
  }

  return numberRelatedViewOptionKeys;
};

// OH 3.4.2025: The product side has decided that this options is confusing to show for external users.
// However, as we are not sure of the internal use case, I won't deprecate the whole feature at this point.
const teamUserNumberRelatedViewOptionKeys = ['hideCurrencyUnits'] as const;

const numberRelatedViewOptionKeys = ['showAllNetFigures', 'showTotalSums', 'showCategoryPercentiles'] as const;

export const visualRelatedViewOptionsKeys = ['hideKeyTakeawayBoxes', 'showDimensionBars', 'showValueChains'] as const;

export const viewOptionKeys = [
  ...teamUserNumberRelatedViewOptionKeys,
  ...numberRelatedViewOptionKeys,
  ...visualRelatedViewOptionsKeys,
] as const;

export type NetImpactLensViewOptionKey = (typeof viewOptionKeys)[number];

export interface NetImpactLensSelectionOptions {
  showAllNetFigures?: boolean | undefined;
  showTotalSums?: boolean | undefined;
  showCategoryPercentiles?: boolean | undefined;
  hideCurrencyUnits?: boolean | undefined;

  hideKeyTakeawayBoxes?: boolean | undefined;
  showDimensionBars?: boolean | undefined;
  showValueChains?: boolean | undefined;
}

export function extractNetImpactLensOptions(selectionState: NetImpactLensSelectionState) {
  return pickBy(
    {
      showAllNetFigures: selectionState.showAllNetFigures,
      showTotalSums: selectionState.showTotalSums,
      showCategoryPercentiles: selectionState.showCategoryPercentiles,
      hideCurrencyUnits: selectionState.hideCurrencyUnits,
      hideKeyTakeawayBoxes: selectionState.hideKeyTakeawayBoxes,
      showDimensionBars: selectionState.showDimensionBars,
      showValueChains: selectionState.showValueChains,
    },
    v => v,
  );
}

export const defaultNetImpactLensScoreUnit = FinalImpactScoreType.CENTS_PER_DOLLAR;

export const defaultNetImpactLensSelectionOptions: NetImpactLensSelectionOptions = {
  showDimensionBars: true,
  showValueChains: false,
  showTotalSums: false,
  showCategoryPercentiles: false,
};

export interface NetImpactLensSelectionState extends NetImpactLensSelectionOptions {
  valence?: Valence;
  impact?: ImpactCategoryId;
}

export function unserializeSelectionState(stateStringValue: string | undefined): NetImpactLensSelectionState {
  if (!stateStringValue) {
    return {};
  }
  const parsed: ParsedUrlQuery = parse(stateStringValue);

  function parseValence(value: string | string[] | undefined): Valence | undefined {
    if (value === Valence.POSITIVE) {
      return value;
    } else if (value === Valence.NEGATIVE) {
      return value;
    }
    return undefined;
  }

  function parseImpactCode(value: string | string[] | undefined) {
    if (!value || Array.isArray(value)) {
      return undefined;
    }
    if (orderedImpacts.indexOf(value as ImpactCategoryId) !== -1) {
      return value as ImpactCategoryId;
    }
    return undefined;
  }

  function parseBoolean(value: string | string[] | undefined) {
    if (value === 'true') {
      return true;
    } else if (value === 'false') {
      return false;
    }
    return undefined;
  }

  return {
    valence: parseValence(parsed.valence),
    impact: parseImpactCode(parsed.impact),
    showAllNetFigures: parseBoolean(parsed.showAllNetFigures),
    showTotalSums: parseBoolean(parsed.showTotalSums),
    showCategoryPercentiles: parseBoolean(parsed.showCategoryPercentiles),
    hideCurrencyUnits: parseBoolean(parsed.hideCurrencyUnits),
    hideKeyTakeawayBoxes: parseBoolean(parsed.hideKeyTakeawayBoxes),
    showDimensionBars: parseBoolean(parsed.showDimensionBars),
    showValueChains: parseBoolean(parsed.showValueChains),
  };
}

export function serializeSelectionState(state: NetImpactLensSelectionState) {
  const string = stringify({
    valence: state.valence,
    impact: state.impact,
    showAllNetFigures: Boolean(state.showAllNetFigures),
    showTotalSums: Boolean(state.showTotalSums),
    showCategoryPercentiles: Boolean(state.showCategoryPercentiles),
    hideCurrencyUnits: Boolean(state.hideCurrencyUnits),
    hideKeyTakeawayBoxes: Boolean(state.hideKeyTakeawayBoxes),
    showDimensionBars: Boolean(state.showDimensionBars),
    showValueChains: Boolean(state.showValueChains),
  });
  return string;
}

function isImpactBarClickable(data: NetImpactLensData, impactId: ImpactCategoryId, valence: Valence) {
  const scores = data.scores;
  if (!scores) {
    return false;
  }
  if (pickScore(scores, impactId, undefined, valence) > 0) {
    return true;
  }
  // Leena-2 debugger FINAL mode: a fully-blacklisted impact has all its leaves
  // removed, so its final score is exactly 0 — but it still carries a positive
  // pre-blacklist baseline in `ghostScores` (the striped ghost tail). Such a
  // bar must stay clickable so the analyst can drill into it like any other.
  // `ghostScores` is undefined in every production profile, so this branch
  // never affects them.
  const ghostScores = data.ghostScores;
  return ghostScores !== undefined && pickScore(ghostScores, impactId, undefined, valence) > 0;
}

function isImpactBarHighlighted(state: NetImpactLensSelectionState, impactId: ImpactCategoryId, valence: Valence) {
  return state.impact === impactId && state.valence === valence;
}

function onImpactBarClick(
  currentSelection: NetImpactLensSelectionState,
  impact: ImpactCategoryId,
  valence: Valence,
): NetImpactLensSelectionState {
  if (currentSelection.impact === impact && currentSelection.valence === valence) {
    return getClearedSelectionState(currentSelection);
  }
  return { ...currentSelection, impact, valence };
}

function showClearSelectionButton(state: NetImpactLensSelectionState) {
  return state.impact !== undefined || state.valence !== undefined;
}

function getClearedSelectionState(state: NetImpactLensSelectionState) {
  return { ...state, valence: undefined, impact: undefined };
}

export const netImpactLensSelectionStateHandlers: Pick<
  ImpactLens<ImpactLensType.UPRIGHT_NET_IMPACT, NetImpactLensData, NetImpactLensOptions>,
  | 'serializeSelectionState'
  | 'unserializeSelectionState'
  | 'onImpactDataAreaClick'
  | 'isImpactDataAreaHighlighted'
  | 'isImpactDataAreaClickable'
  | 'showClearSelectionButton'
  | 'getClearedSelectionState'
> = {
  serializeSelectionState,
  unserializeSelectionState,
  onImpactDataAreaClick: onImpactBarClick,
  isImpactDataAreaHighlighted: isImpactBarHighlighted,
  isImpactDataAreaClickable: isImpactBarClickable,
  showClearSelectionButton,
  getClearedSelectionState,
};
